<template>
  <div class="official-reg-root">
    <MainNavbar />
    <div class="official-reg-page">
      <router-link :to="{ name: 'MatchesHub' }" class="official-reg-back">
        <i class="fa-solid fa-arrow-left" aria-hidden="true" />
        Back to Match Hub
      </router-link>

      <header class="official-reg-header">
        <p class="official-reg-kicker">
          <i class="fa-solid fa-scale-balanced" aria-hidden="true" />
          IpromiseAfrica Match Hub · Officials
        </p>
        <h1 class="official-reg-title">Register as a match official</h1>
        <p class="official-reg-lead">
          Join the pool of referees, umpires, and table officials who keep fixtures fair across
          football, basketball, rugby, volleyball, and tennis on Match Hub.
        </p>
      </header>

      <aside class="official-reg-earnings" aria-label="Earnings information">
        <div class="official-reg-earnings__icon" aria-hidden="true">
          <i class="fa-solid fa-coins" />
        </div>
        <div>
          <h2 class="official-reg-earnings__title">Earn while you officiate</h2>
          <p class="official-reg-earnings__body">
            The goal of this programme is to help you earn a little cash for officiating matches.
            Typical fees start from <strong>KSh 1,000 per match</strong> and often go higher—the
            two opposing sides agree what they will pay before you accept an assignment. Final pay
            depends on the fixture level, travel, crew size, and what teams are willing to offer.
          </p>
        </div>
      </aside>

      <div v-if="!submitted" class="official-reg-card">
        <div class="official-reg-steps" role="list" aria-label="Registration progress">
          <div
            v-for="(step, index) in steps"
            :key="step"
            class="official-reg-step"
            role="listitem"
            :class="{ active: currentStep === index + 1, done: currentStep > index + 1 }"
          >
            <span class="official-reg-step__num">{{ index + 1 }}</span>
            <span class="official-reg-step__label">{{ step }}</span>
          </div>
        </div>

        <!-- Step 1: Sport -->
        <section v-show="currentStep === 1" aria-labelledby="official-step1-title">
          <h2 id="official-step1-title" class="official-reg-panel__title">Choose your sport</h2>
          <p class="official-reg-panel__desc">
            Pick the category you are qualified—or training—to officiate. You can register for one
            sport now; more sports may be added to your profile later.
          </p>

          <div class="official-reg-sport-grid" role="radiogroup" aria-label="Sport category">
            <button
              v-for="sport in sports"
              :key="sport.slug"
              type="button"
              class="official-reg-sport-card"
              :class="{ selected: form.sport === sport.slug }"
              :style="form.sport === sport.slug ? { '--sport-accent': sport.accent } : {}"
              :aria-pressed="form.sport === sport.slug"
              @click="selectSport(sport.slug)"
            >
              <i :class="['official-reg-sport-card__icon', sport.icon]" aria-hidden="true" />
              <span class="official-reg-sport-card__name">{{ sport.name }}</span>
              <span class="official-reg-sport-card__hint">{{ sport.description }}</span>
            </button>
          </div>
          <span v-if="errors.sport" class="official-reg-error">{{ errors.sport }}</span>

          <div class="official-reg-nav">
            <span />
            <button type="button" class="official-reg-btn official-reg-btn--primary" @click="nextFromSport">
              Continue <i class="fa-solid fa-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </section>

        <!-- Step 2: Official type -->
        <section v-show="currentStep === 2" aria-labelledby="official-step2-title">
          <h2 id="official-step2-title" class="official-reg-panel__title">Official role</h2>
          <p class="official-reg-panel__desc">
            For <strong>{{ selectedSportName }}</strong>, select the type of official you want to be
            listed as. Options change by sport.
          </p>

          <div
            v-if="availableRoles.length"
            class="official-reg-role-grid"
            role="radiogroup"
            :aria-label="`Official roles for ${selectedSportName}`"
          >
            <button
              v-for="role in availableRoles"
              :key="role.id"
              type="button"
              class="official-reg-role-card"
              :class="{ selected: form.officialType === role.id }"
              :aria-pressed="form.officialType === role.id"
              @click="form.officialType = role.id"
            >
              <span class="official-reg-role-card__icon" aria-hidden="true">
                <i :class="role.icon" />
              </span>
              <span>
                <span class="official-reg-role-card__label">{{ role.label }}</span>
                <span class="official-reg-role-card__desc">{{ role.description }}</span>
              </span>
            </button>
          </div>
          <p v-else class="official-reg-hint">Select a sport in step 1 to see available roles.</p>
          <span v-if="errors.officialType" class="official-reg-error">{{ errors.officialType }}</span>

          <div class="official-reg-nav">
            <button type="button" class="official-reg-btn official-reg-btn--ghost" @click="currentStep = 1">
              <i class="fa-solid fa-arrow-left" aria-hidden="true" /> Back
            </button>
            <button type="button" class="official-reg-btn official-reg-btn--primary" @click="nextFromRole">
              Continue <i class="fa-solid fa-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </section>

        <!-- Step 3: Profile -->
        <section v-show="currentStep === 3" aria-labelledby="official-step3-title">
          <h2 id="official-step3-title" class="official-reg-panel__title">Your profile</h2>
          <p class="official-reg-panel__desc">
            Tell teams about your officiating experience. Your name, phone, and email come from your
            signed-in IPA customer account.
          </p>

          <p v-if="accountContact.name || accountContact.email" class="official-reg-notice">
            <i class="fa-solid fa-user" aria-hidden="true" />
            <span>
              Registering as
              <strong>{{ accountContact.name || "your account" }}</strong>
              <template v-if="accountContact.email"> ({{ accountContact.email }})</template>
            </span>
          </p>

          <div class="official-reg-form-grid">
            <div class="official-reg-field">
              <label for="experience">Experience level *</label>
              <select
                id="experience"
                v-model="form.experience"
                :class="{ error: errors.experience }"
              >
                <option value="" disabled>Select level</option>
                <option v-for="lvl in experienceLevels" :key="lvl.value" :value="lvl.value">
                  {{ lvl.label }}
                </option>
              </select>
              <span v-if="errors.experience" class="official-reg-error">{{ errors.experience }}</span>
            </div>

            <div class="official-reg-field">
              <label for="licenseId">License / badge no. (optional)</label>
              <input
                id="licenseId"
                v-model.trim="form.licenseId"
                type="text"
                placeholder="e.g. FKF, KBF, or association ID"
              />
              <p class="official-reg-hint">If you hold a federation or county badge, add it here.</p>
            </div>

            <div class="official-reg-field official-reg-field--full">
              <label for="bio">Short bio *</label>
              <textarea
                id="bio"
                v-model.trim="form.bio"
                rows="4"
                maxlength="400"
                placeholder="Years officiating, leagues covered, languages spoken..."
                :class="{ error: errors.bio }"
              />
              <span v-if="errors.bio" class="official-reg-error">{{ errors.bio }}</span>
            </div>
          </div>

          <div class="official-reg-nav">
            <button type="button" class="official-reg-btn official-reg-btn--ghost" @click="currentStep = 2">
              <i class="fa-solid fa-arrow-left" aria-hidden="true" /> Back
            </button>
            <button type="button" class="official-reg-btn official-reg-btn--primary" @click="nextFromProfile">
              Continue <i class="fa-solid fa-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </section>

        <!-- Step 4: Location, availability & pay -->
        <section v-show="currentStep === 4" aria-labelledby="official-step4-title">
          <h2 id="official-step4-title" class="official-reg-panel__title">Location, availability & fees</h2>
          <p class="official-reg-panel__desc">
            Tell teams where you are based, when you are free, and your minimum fee. County and
            constituency help with travel planning, fuel estimates, and match-day logistics.
          </p>

          <p class="official-reg-notice official-reg-notice--logistics">
            <i class="fa-solid fa-route" aria-hidden="true" />
            Teams use your <strong>county</strong> and <strong>constituency</strong> to estimate travel
            time, assign nearby fixtures, and coordinate transport or allowances before they confirm
            you for a match.
          </p>

          <div class="official-reg-form-grid official-reg-form-grid--location">
            <div class="official-reg-field">
              <label for="county">County *</label>
              <select
                id="county"
                v-model="form.county"
                :class="{ error: errors.county }"
                @change="onCountyChange"
              >
                <option value="" disabled>Select county</option>
                <option v-for="c in availableCounties" :key="c" :value="c">{{ c }}</option>
              </select>
              <span v-if="errors.county" class="official-reg-error">{{ errors.county }}</span>
            </div>

            <div class="official-reg-field">
              <label for="constituency">Constituency *</label>
              <select
                id="constituency"
                v-model="form.constituency"
                :disabled="!form.county"
                :class="{ error: errors.constituency }"
              >
                <option value="" disabled>
                  {{ form.county ? "Select constituency" : "Select county first" }}
                </option>
                <option v-for="c in availableConstituencies" :key="c" :value="c">{{ c }}</option>
              </select>
              <p class="official-reg-hint">Your usual base area for fixture assignments.</p>
              <span v-if="errors.constituency" class="official-reg-error">{{ errors.constituency }}</span>
            </div>
          </div>

          <div class="official-reg-field official-reg-field--full">
            <label>Days you can officiate *</label>
            <div class="official-reg-days" role="group" aria-label="Available days">
              <button
                v-for="day in dayOptions"
                :key="day.value"
                type="button"
                class="official-reg-day-chip"
                :class="{ selected: form.availableDays.includes(day.value) }"
                :aria-pressed="form.availableDays.includes(day.value)"
                @click="toggleDay(day.value)"
              >
                {{ day.label }}
              </button>
            </div>
            <span v-if="errors.availableDays" class="official-reg-error">{{ errors.availableDays }}</span>
          </div>

          <div class="official-reg-form-grid" style="margin-top: 20px">
            <div class="official-reg-field">
              <label for="travelCounties">Other counties you can travel to (optional)</label>
              <input
                id="travelCounties"
                v-model.trim="form.travelCounties"
                type="text"
                placeholder="e.g. Kiambu, Machakos, Kajiado"
              />
              <p class="official-reg-hint">
                Beyond {{ form.county || "your home county" }}—helps teams book you for away fixtures.
              </p>
            </div>

            <div class="official-reg-field">
              <label for="minFee">Minimum fee expectation *</label>
              <select id="minFee" v-model="form.minFee" :class="{ error: errors.minFee }">
                <option value="" disabled>Select range</option>
                <option v-for="opt in minFeeOptions" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
              <p class="official-reg-hint">
                Teams may offer more than your minimum for cup games or long travel.
              </p>
              <span v-if="errors.minFee" class="official-reg-error">{{ errors.minFee }}</span>
            </div>
          </div>

          <label class="official-reg-check">
            <input v-model="form.termsAccepted" type="checkbox" />
            <span>
              I understand that IpromiseAfrica Match Hub connects officials with teams; payment terms are
              agreed directly between me and the opposing sides before each fixture, typically from
              <strong>KSh 1,000</strong> upward depending on what they offer.
            </span>
          </label>
          <span v-if="errors.termsAccepted" class="official-reg-error">{{ errors.termsAccepted }}</span>

          <div class="official-reg-nav">
            <button type="button" class="official-reg-btn official-reg-btn--ghost" @click="currentStep = 3">
              <i class="fa-solid fa-arrow-left" aria-hidden="true" /> Back
            </button>
            <button type="button" class="official-reg-btn official-reg-btn--primary" @click="goToReview">
              Review <i class="fa-solid fa-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </section>

        <!-- Step 5: Review -->
        <section v-show="currentStep === 5" aria-labelledby="official-step5-title">
          <h2 id="official-step5-title" class="official-reg-panel__title">Review & submit</h2>
          <p class="official-reg-panel__desc">
            Confirm your details, then submit to join the match officials pool.
          </p>

          <div class="official-reg-review">
            <div class="official-reg-review-row">
              <span>Sport</span>
              <strong>{{ selectedSportName }}</strong>
            </div>
            <div class="official-reg-review-row">
              <span>Official role</span>
              <strong>{{ selectedRoleLabel }}</strong>
            </div>
            <div class="official-reg-review-row">
              <span>Account contact</span>
              <strong>{{ accountContactSummary }}</strong>
            </div>
            <div class="official-reg-review-row">
              <span>Base location</span>
              <strong>{{ locationSummary }}</strong>
            </div>
            <div class="official-reg-review-row" v-if="form.travelCounties">
              <span>Also travels to</span>
              <strong>{{ form.travelCounties }}</strong>
            </div>
            <div class="official-reg-review-row">
              <span>Experience</span>
              <strong>{{ experienceLabel }}</strong>
            </div>
            <div class="official-reg-review-row">
              <span>Available days</span>
              <strong>{{ formattedDays }}</strong>
            </div>
            <div class="official-reg-review-row">
              <span>Minimum fee</span>
              <strong>{{ minFeeLabel }}</strong>
            </div>
          </div>

          <p class="official-reg-notice">
            <i class="fa-solid fa-circle-info" aria-hidden="true" />
            Your profile is reviewed before teams can book you for fixtures. Contact details are taken
            from your IPA customer account.
          </p>
          <p v-if="submitError" class="official-reg-error official-reg-error--block">
            {{ submitError }}
          </p>

          <div class="official-reg-nav">
            <button type="button" class="official-reg-btn official-reg-btn--ghost" @click="currentStep = 4">
              <i class="fa-solid fa-arrow-left" aria-hidden="true" /> Back
            </button>
            <button
              type="button"
              class="official-reg-btn official-reg-btn--primary"
              :disabled="isSubmitting"
              @click="submitRegistration"
            >
              <i v-if="isSubmitting" class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
              <template v-else>
                Submit registration <i class="fa-solid fa-paper-plane" aria-hidden="true" />
              </template>
            </button>
          </div>
        </section>
      </div>

      <div v-else class="official-reg-card official-reg-success">
        <i class="fa-solid fa-circle-check" aria-hidden="true" />
        <h2>Registration submitted</h2>
        <p>
          Thanks, {{ accountContact.name || "official" }}. Your {{ selectedSportName }}
          {{ selectedRoleLabel }} profile is registered. On Match Hub you will find a page of
          confirmed fixtures—each pairing the challenger with their opponent—so you can choose
          whichever assignments suit you for officiating.
        </p>
        <router-link :to="{ name: 'MatchesHub' }" class="official-reg-btn official-reg-btn--primary">
          Back to Match Hub
        </router-link>
      </div>
    </div>
    <MainFooter />
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import MainFooter from "@/components/main/headernfooter/Footer.vue";
import { buildApiUrl } from "@/config/api";
import { useAuthStore } from "@/authen/auth";
import { buildSignInLocation } from "@/utils/authRedirect";
import {
  OFFICIAL_SPORTS,
  OFFICIAL_TYPES_BY_SPORT,
  OFFICIAL_EXPERIENCE_LEVELS,
  OFFICIAL_AVAILABILITY_DAYS,
  OFFICIAL_MIN_FEE_OPTIONS,
} from "@/routes/match_officials/constants";
import { kenyaCounties } from "@/routes/match_hub/football/counties.js";

const DAY_LABELS = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

export default {
  name: "MatchOfficialRegistration",
  components: { MainNavbar, MainFooter },

  data() {
    return {
      sports: OFFICIAL_SPORTS,
      experienceLevels: OFFICIAL_EXPERIENCE_LEVELS,
      minFeeOptions: OFFICIAL_MIN_FEE_OPTIONS,
      steps: ["Sport", "Role", "Profile", "Location", "Review"],
      availableCounties: Object.keys(kenyaCounties),
      availableConstituencies: [],
      currentStep: 1,
      submitted: false,
      isSubmitting: false,
      submitError: "",
      errors: {},
      accountContact: {
        name: "",
        email: "",
        phone: "",
      },
      form: {
        sport: "",
        officialType: "",
        county: "",
        constituency: "",
        experience: "",
        licenseId: "",
        bio: "",
        availableDays: [],
        travelCounties: "",
        minFee: "",
        termsAccepted: false,
      },
    };
  },

  computed: {
    dayOptions() {
      return OFFICIAL_AVAILABILITY_DAYS.map((value) => ({
        value,
        label: DAY_LABELS[value] || value,
      }));
    },

    selectedSportName() {
      return this.sports.find((s) => s.slug === this.form.sport)?.name || "";
    },

    availableRoles() {
      if (!this.form.sport) return [];
      return OFFICIAL_TYPES_BY_SPORT[this.form.sport] || [];
    },

    selectedRoleLabel() {
      const role = this.availableRoles.find((r) => r.id === this.form.officialType);
      return role?.label || "—";
    },

    experienceLabel() {
      return (
        this.experienceLevels.find((l) => l.value === this.form.experience)?.label ||
        this.form.experience
      );
    },

    minFeeLabel() {
      return this.minFeeOptions.find((o) => o.value === this.form.minFee)?.label || "—";
    },

    formattedDays() {
      if (!this.form.availableDays.length) return "—";
      return this.form.availableDays
        .map((d) => DAY_LABELS[d] || d)
        .join(", ");
    },

    locationSummary() {
      if (!this.form.county && !this.form.constituency) return "—";
      if (this.form.county && this.form.constituency) {
        return `${this.form.county}, ${this.form.constituency}`;
      }
      return this.form.county || this.form.constituency;
    },

    accountContactSummary() {
      const parts = [this.accountContact.name, this.accountContact.phone, this.accountContact.email]
        .filter(Boolean);
      return parts.length ? parts.join(" · ") : "From your IPA account";
    },
  },

  mounted() {
    this.ensureAuthenticated();
    this.loadAccountContact();
  },

  methods: {
    getAuthToken() {
      const auth = useAuthStore();
      auth.checkToken();
      return auth.accessToken || null;
    },

    ensureAuthenticated() {
      const auth = useAuthStore();
      auth.checkToken();
      if (!auth.isAuthenticated) {
        this.$router.replace(buildSignInLocation(this.$route.fullPath));
      }
    },

    async loadAccountContact() {
      const auth = useAuthStore();
      const stored = auth.user;
      if (stored && typeof stored === "object") {
        this.accountContact = {
          name: stored.full_name || stored.name || "",
          email: stored.email || "",
          phone: stored.phone_number || stored.phone || "",
        };
      }

      const token = this.getAuthToken();
      if (!token) return;

      try {
        const response = await fetch(buildApiUrl("/auth/me"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const data = await response.json();
        this.accountContact = {
          name: data.name || data.full_name || this.accountContact.name,
          email: data.email || this.accountContact.email,
          phone: data.phone_number || data.phone || this.accountContact.phone,
        };
      } catch {
        /* optional enrichment — auth store is enough for review */
      }
    },
    selectSport(slug) {
      if (this.form.sport !== slug) {
        this.form.sport = slug;
        this.form.officialType = "";
      }
    },

    toggleDay(day) {
      const idx = this.form.availableDays.indexOf(day);
      if (idx === -1) {
        this.form.availableDays.push(day);
      } else {
        this.form.availableDays.splice(idx, 1);
      }
    },

    onCountyChange() {
      this.form.constituency = "";
      this.availableConstituencies = kenyaCounties[this.form.county] || [];
    },

    clearErrors() {
      this.errors = {};
    },

    nextFromSport() {
      this.clearErrors();
      if (!this.form.sport) {
        this.errors.sport = "Please select a sport category.";
        return;
      }
      this.currentStep = 2;
    },

    nextFromRole() {
      this.clearErrors();
      if (!this.form.officialType) {
        this.errors.officialType = "Please select an official role for this sport.";
        return;
      }
      this.currentStep = 3;
    },

    nextFromProfile() {
      this.clearErrors();
      if (!this.form.experience) {
        this.errors.experience = "Select your experience level.";
      }
      if (!this.form.bio.trim() || this.form.bio.trim().length < 30) {
        this.errors.bio = "Bio must be at least 30 characters.";
      }
      if (Object.keys(this.errors).length) return;
      this.currentStep = 4;
    },

    goToReview() {
      this.clearErrors();
      if (!this.form.county) {
        this.errors.county = "County is required for travel and logistics.";
      }
      if (!this.form.constituency) {
        this.errors.constituency = "Constituency is required for travel and logistics.";
      }
      if (!this.form.availableDays.length) {
        this.errors.availableDays = "Select at least one day you are available.";
      }
      if (!this.form.minFee) {
        this.errors.minFee = "Select your minimum fee expectation.";
      }
      if (!this.form.termsAccepted) {
        this.errors.termsAccepted = "Please confirm you understand how match fees work.";
      }
      if (Object.keys(this.errors).length) return;
      this.currentStep = 5;
    },

    async submitRegistration() {
      this.submitError = "";
      this.isSubmitting = true;
      try {
        this.ensureAuthenticated();
        const token = this.getAuthToken();

        if (!token) {
          this.$router.replace(buildSignInLocation(this.$route.fullPath));
          return;
        }

        const response = await fetch(buildApiUrl("/api/match-officials/register"), {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sport: this.form.sport,
            official_type: this.form.officialType,
            county: this.form.county,
            constituency: this.form.constituency,
            experience: this.form.experience,
            license_id: this.form.licenseId || null,
            bio: this.form.bio,
            available_days: this.form.availableDays,
            travel_counties: this.form.travelCounties || null,
            min_fee: this.form.minFee,
            terms_accepted: this.form.termsAccepted,
          }),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          const detail = data.detail;
          if (Array.isArray(detail)) {
            this.submitError = detail.map((d) => d.msg || d.message).filter(Boolean).join(" ");
          } else if (typeof detail === "string") {
            this.submitError = detail;
          } else {
            this.submitError = data.message || "Registration failed. Please try again.";
          }
          return;
        }

        if (data.full_name || data.email || data.phone) {
          this.accountContact = {
            name: data.full_name || this.accountContact.name,
            email: data.email || this.accountContact.email,
            phone: data.phone || this.accountContact.phone,
          };
        }

        this.submitted = true;
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (err) {
        console.error("Match official registration failed:", err);
        this.submitError = "Network error. Check your connection and try again.";
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped src="./registerOfficial.css"></style>
